<template>
    <component
        :is="href ? 'a' : 'button'"
        v-tooltip="tooltip"
        v-bind="$attrs"
        :href="href"
        :dusk="dusk"
        :type="href ? null : 'button'"
        :class="[
            containerClasses,
            'focus-visible:ring-2',
            'focus-visible:ring-theme-accent',
            'focus-visible:ring-offset-2',
            unstyled ? 'focus:outline-none' : { 'focus:outline-none': variant, rounded: !variant && rounded },
        ]"
        :aria-label="effectiveAriaLabel"
        :data-action="dataAction"
        :data-float-label="dataFloatLabel"
        :disabled="disabled"
        @click="emitClick"
    >
        <slot>
            <svg-icon
                :key="icon"
                :style="iconCustomStyle"
                :fill="iconCustomFill"
                :icon="icon"
                :width="width"
                :height="height"
            />
            <span v-if="btnText" class="ml-3 text-theme-text">{{ btnText }}</span>
        </slot>
    </component>
</template>
<script>
const VARIANT_CLASSES = {
    primary:
        'flex items-center justify-center gap-2 rounded-lg bg-theme-accent text-sm font-semibold text-theme-accent-on transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40',
    secondary:
        'flex items-center justify-center gap-2 rounded-lg border border-theme-accent text-sm font-medium text-theme-accent transition-colors hover:bg-theme-accent hover:text-theme-accent-on',
    ghost: 'rounded text-sm text-theme-toolbar-icon underline-offset-4 transition-colors hover:text-theme-text hover:underline',
    cta: 'inline-flex flex-none items-center justify-center gap-2 rounded-full bg-theme-buynow-bg text-sm font-semibold text-theme-buynow-text shadow-sm transition hover:opacity-90',
    danger: 'flex items-center justify-center gap-2 rounded-lg bg-theme-danger-fill text-sm font-semibold text-theme-danger-on transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40',
};

/* Padding lives apart from the variant so a caller can ask for a tighter button without
   fighting the shell's own classes over which px-* wins in the cascade. */
const VARIANT_PADDING_CLASSES = {
    primary: { md: 'px-4 py-3', sm: 'px-2.5 py-1.5' },
    secondary: { md: 'px-4 py-3', sm: 'px-2.5 py-1.5' },
    ghost: { md: 'px-2 py-1', sm: 'px-1.5 py-0.5' },
    cta: { md: 'px-3.5 py-1', sm: 'px-2.5 py-0.5' },
    danger: { md: 'px-4 py-3', sm: 'px-2.5 py-1.5' },
};
const DEFAULT_BUTTON_CLASSES = 'flex items-center flex-row justify-start p-2 hover:bg-theme-icon-bg';

export default {
    inheritAttrs: false,
    props: {
        width: {
            type: String,
            required: false,
            default: '24px',
        },
        height: {
            type: String,
            required: false,
            default: '24px',
        },
        dusk: {
            type: String,
            required: false,
            default: null,
        },
        btnText: {
            type: String,
            required: false,
            default: null,
        },
        icon: {
            type: String,
            required: false,
            default: null,
        },
        iconCustomStyle: {
            type: [String, Object],
            required: false,
            default: null,
        },
        iconCustomFill: {
            type: String,
            required: false,
            default: null,
        },
        dataAction: {
            type: String,
            required: false,
            default: null,
        },
        dataFloatLabel: {
            type: String,
            required: false,
            default: null,
        },
        tooltip: {
            type: String,
            required: false,
            default: null,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        rounded: {
            type: Boolean,
            required: false,
            default: true,
        },
        ariaLabel: {
            type: String,
            required: false,
            default: null,
        },
        // Renders a link instead, for an action that takes the user somewhere.
        href: {
            type: String,
            required: false,
            default: null,
        },
        variant: {
            type: String,
            required: false,
            default: null,
            validator: value => value === null || ['primary', 'secondary', 'ghost', 'cta', 'danger'].includes(value),
        },
        /**
         * How much padding the variant's shell carries. 'sm' tightens it for cramped rows.
         */
        size: {
            type: String,
            required: false,
            default: 'md',
            validator: value => ['sm', 'md'].includes(value),
        },
        unstyled: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    emits: ['click'],
    computed: {
        effectiveAriaLabel() {
            if (this.ariaLabel) return this.ariaLabel;
            if (!this.btnText && this.tooltip) return this.tooltip;
            return null;
        },
        /**
         * Layout/color classes for the button shell. Named variants are the reusable
         * primary/secondary/ghost/cta/danger action styles; the null variant reproduces the legacy
         * icon-button layout exactly (no visual change for existing callers). With
         * `unstyled` there is no shell at all: the caller supplies its own classes while
         * keeping the centralized focus ring, aria and dusk behavior.
         * @returns {string} Tailwind class string for the button shell (without the focus ring)
         */
        containerClasses() {
            if (this.unstyled) return '';
            if (!VARIANT_CLASSES[this.variant]) return DEFAULT_BUTTON_CLASSES;
            return `${VARIANT_CLASSES[this.variant]} ${VARIANT_PADDING_CLASSES[this.variant][this.size]}`;
        },
    },
    methods: {
        emitClick() {
            this.$emit('click');
        },
    },
};
</script>
